import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import type { CoverageLookupHit } from "@/lib/coverage/coverageLookup";
import type { CoverageSuggestion } from "@/lib/coverage/coverageNotes";
import { externalMapSearchUrlFromLatLng } from "@/lib/maps/externalLinks";

type Props = {
  address: string;
  program: string;
  sizeLabel: string;
  busy: boolean;
  error: string | null;
  precision: "rooftop" | "city" | null;
  point: { lat: number; lng: number } | null;
  hits: CoverageLookupHit[];
  suggestion: CoverageSuggestion | null;
  suggestBusy: boolean;
  suggestError: string | null;
  chosenPartyId: string | null;
  onAddress: (next: string) => void;
  onProgram: (next: string) => void;
  onSizeLabel: (next: string) => void;
  onLookup: () => void;
  onSuggest: () => void;
  onChoose: (partyId: string) => void;
};

export function CoverageLookupCard(props: Props) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Who covers this address</h3>
      <div className="mt-2 space-y-2">
        <AddressAutocompleteInput
          value={props.address}
          onChange={props.onAddress}
          placeholder="Delivery address, city, or ZIP"
        />
        <label className="block text-xs text-gray-500 dark:text-zinc-400">
          Program
          <input
            value={props.program}
            onChange={(e) => props.onProgram(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
          />
        </label>
        <label className="block text-xs text-gray-500 dark:text-zinc-400">
          Size
          <input
            value={props.sizeLabel}
            onChange={(e) => props.onSizeLabel(e.target.value)}
            placeholder="Optional, such as 10x20"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={props.busy || !props.address.trim()}
            onClick={props.onLookup}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          >
            {props.busy ? "Looking up…" : "Look up"}
          </button>
          <button
            type="button"
            disabled={props.suggestBusy || !props.address.trim()}
            onClick={props.onSuggest}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:text-zinc-100 disabled:opacity-50"
          >
            {props.suggestBusy ? "Suggesting…" : "Suggest"}
          </button>
        </div>
        {props.error ? <p className="text-sm text-red-600">{props.error}</p> : null}
        {props.suggestError ? <p className="text-sm text-red-600">{props.suggestError}</p> : null}
        {props.suggestion && props.chosenPartyId && props.chosenPartyId !== props.suggestion.partyId ? (
          <p className="text-sm text-gray-700 dark:text-zinc-200">You chose a different provider than the one suggested.</p>
        ) : null}
        {!props.error && props.point && props.hits.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-300">No provider covers that pin.</p>
        ) : null}
        {props.precision === "city" ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">This pin is a city, not a driveway.</p>
        ) : null}
      </div>
      {props.hits.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {props.hits.map((hit) => (
            <li key={hit.partyId} className="rounded-md border border-gray-100 p-2 text-sm dark:border-zinc-800">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: hit.color }} />
                <span className="font-medium text-gray-900 dark:text-zinc-100">{hit.name}</span>
                <span className="text-xs uppercase tracking-wide text-gray-500">{hit.tier}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                {hit.milesFromPlant == null ? "Distance unavailable" : `${hit.milesFromPlant} miles from the plant`}
              </p>
              {props.suggestion?.partyId === hit.partyId ? (
                <p className="mt-1 text-xs text-gray-700 dark:text-zinc-200">{props.suggestion.reason}</p>
              ) : null}
              <button
                type="button"
                className="mt-1 text-sm text-blue-700 dark:text-blue-300"
                onClick={() => props.onChoose(hit.partyId)}
              >
                {props.chosenPartyId === hit.partyId ? "Chosen" : "Choose this provider"}
              </button>
              {hit.pricingUrl ? (
                <a className="mt-1 inline-block text-sm text-blue-700 underline dark:text-blue-300" href={hit.pricingUrl} target="_blank" rel="noreferrer">
                  Open pricing
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {props.point ? (
        <a
          className="mt-3 inline-block text-sm text-blue-700 underline dark:text-blue-300"
          href={externalMapSearchUrlFromLatLng(props.point)}
          target="_blank"
          rel="noreferrer"
        >
          Open this pin in Google Maps
        </a>
      ) : null}
    </section>
  );
}
